import { TaxInsightBlock, TaxInsightGroup, taxInsightBlocks, taxInsightGroup } from '../tax-accountancy-company-profile/tax-profile-insights';

/** The play rendered as the always-visible credit band rather than as a view tab. */
export const FINANCIAL_SERVICES_CREDIT_PLAY = 'credit_screen';

/**
 * Short descriptions for the view tabs. Labels, order and the questions in each view come from
 * financial_services_profile_insights, so a view added on the backend appears without a frontend change.
 */
export const FINANCIAL_SERVICES_VIEW_DESCRIPTIONS: Record<string, string> = {
  fx_trade: 'Currency flows, hedging, providers and trade',
  lending: 'Facilities, refinancing and borrowing needs',
  working_capital: 'Customers, payment terms, assets and capex',
  ownership: 'Owners, group, exits and trigger events',
  cash_treasury: 'Cash position, treasury and finance team',
};

/** One view from the endpoint: its label and the answered questions as groups and layout blocks. */
export interface FinancialServicesView {
  id: string;
  label: string;
  description: string;
  questionCount: number;
  groups: TaxInsightGroup[];
  blocks: TaxInsightBlock[];
}

export interface FinancialServicesInsights {
  credit: FinancialServicesView | null;
  views: FinancialServicesView[];
}

function toView(play: any): FinancialServicesView {
  const responses = Array.isArray(play?.responses) ? play.responses : [];
  // The endpoint has no Data API keys to identify questions yet, so key groups by its stable question_key.
  const groups = responses
    .filter((response: any) => response && typeof response === 'object')
    .map((response: any) => taxInsightGroup({ ...response, question_data_api_key: response.question_data_api_key || response.question_key }));
  const id = String(play?.id || '');
  return {
    id,
    label: String(play?.label || ''),
    description: FINANCIAL_SERVICES_VIEW_DESCRIPTIONS[id] || '',
    questionCount: Number(play?.question_count) || 0,
    groups,
    blocks: taxInsightBlocks(groups),
  };
}

/** Splits the endpoint's plays into the credit band and the view tabs, in the order the backend sends them. */
export function financialServicesInsights(payload: unknown): FinancialServicesInsights {
  const plays = Array.isArray(payload) ? payload.filter(play => play && typeof play === 'object' && play.id) : [];
  const credit = plays.find(play => play.id === FINANCIAL_SERVICES_CREDIT_PLAY);
  return {
    credit: credit ? toView(credit) : null,
    views: plays.filter(play => play.id !== FINANCIAL_SERVICES_CREDIT_PLAY).map(toView),
  };
}

/**
 * Banking segment from revenue, matching the brief's presets. Estimated revenue still gives a segment, so the label
 * says which it is.
 */
export function bankingSegment(revenue: unknown): { label: string; range: string } | null {
  // The overview shows revenue as text such as "£38.4M" or "£750k", so read the unit as well as the digits.
  const text = String(revenue ?? '').toLowerCase().replace(/,/g, '');
  const match = text.match(/([0-9.]+)\s*(bn|b|m|k)?/);
  const unit = { bn: 1e9, b: 1e9, m: 1e6, k: 1e3 }[match?.[2] || ''] || 1;
  const value = typeof revenue === 'number' ? revenue : match ? Number(match[1]) * unit : NaN;
  if (!Number.isFinite(value) || value <= 0) return null;
  if (value < 750_000) return { label: 'Business banking', range: 'Under £750k' };
  if (value < 6_500_000) return { label: 'Lower commercial', range: '£750k to £6.5m' };
  if (value < 25_000_000) return { label: 'Mid-market', range: '£6.5m to £25m' };
  if (value < 100_000_000) return { label: 'Upper mid-market', range: '£25m to £100m' };
  if (value < 500_000_000) return { label: 'Corporate', range: '£100m to £500m' };
  return { label: 'Large corporate', range: '£500m and over' };
}
